import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { CircularProgress, InputAdornment } from '@mui/material';
import type { ReactNode } from 'react';
import type { GrowerIdentifier, IdentifierFieldStatus } from './grower-identifier';
import { getGrowerIdentifierCopy } from './grower-identifier';

export function IdentifierStatusAdornment({ status }: { status: IdentifierFieldStatus }) {
    if (status === 'verifying') {
        return (
            <InputAdornment position='end'>
                <CircularProgress size={18} color='inherit' />
            </InputAdornment>
        );
    }
    if (status === 'valid') {
        return (
            <InputAdornment position='end'>
                <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />
            </InputAdornment>
        );
    }
    if (status === 'duplicate' || status === 'invalidFormat' || status === 'connectionError') {
        return (
            <InputAdornment position='end'>
                <ErrorOutlineIcon sx={{ color: 'error.main', fontSize: 20 }} />
            </InputAdornment>
        );
    }
    return null;
}

export function getIdentifierStatusHelperText({
    identifier,
    status,
    businessName,
    digitCount,
    cuitLength,
    /** Specific message from validateCuit / validateGrowerIdentifierEmail when status is invalidFormat. */
    formatErrorMessage,
}: {
    identifier: GrowerIdentifier;
    status: IdentifierFieldStatus;
    businessName?: string;
    digitCount?: number;
    cuitLength?: number;
    formatErrorMessage?: string;
}): ReactNode {
    const copy = getGrowerIdentifierCopy(identifier);

    switch (status) {
        case 'verifying':
            return copy.verifying;
        case 'valid':
            return copy.valid(businessName);
        case 'duplicate':
            return copy.alreadyRegistered;
        case 'invalidFormat':
            return formatErrorMessage ?? copy.invalidFormat;
        case 'connectionError':
            return copy.connectionError;
        default:
            if (identifier === 'cuit' && digitCount != null && cuitLength != null) {
                return `${copy.fieldHelp} (${digitCount} / ${cuitLength})`;
            }
            return copy.fieldHelp;
    }
}

export function isIdentifierStatusError(status: IdentifierFieldStatus): boolean {
    return status === 'duplicate' || status === 'invalidFormat' || status === 'connectionError';
}
